import "../../../../../styles/profile/readyForReview.less";
import { useContext, useEffect, useMemo, useState } from "react";

import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Collapse, Spin, Typography } from "antd";
import { AppContext } from "App";
import pdfIcon from "assets/images/new-questionnaire/documentParse/pdf_icon.svg";
import openLinkIcon from "assets/images/new-questionnaire/open_link_icon.svg";
import { useGetTaxCalculationsMutation } from "services/client";
import { IUser } from "types/auth";
import { IClientDocument } from "types/client";
import { logError } from "utils/sentry";

import { useGeneratePdfMutation } from "../../../../../services/pdf";
import { generatePdfFromBlob, getPdfHtml } from "../../../../../utils/pdf-from-html";

type ReadyForReviewOweTaxProps = {
    currentDocumentYear: string;
    user: IUser;
    currentDocument: IClientDocument
};
export default function SubmittedToHMRC({ currentDocumentYear, user, currentDocument }: ReadyForReviewOweTaxProps) {
    const { isMobile } = useContext(AppContext);
    const date = new Date().toLocaleDateString("en-GB");
    const [isDownloading, setIsDownloading] = useState(false);
    const { Panel } = Collapse;
    const [taxData, setTaxData] = useState<any | null>(null);
    const [generatePdf] = useGeneratePdfMutation();
    const [getGetTaxCalculations] = useGetTaxCalculationsMutation();
    const taxLiability = taxData?.totalTaxLiability?.value;
    const paymentOnAccountJan = taxData?.paymentsOnAccount31stJan?.value;
    const paymentOnAccountJuly = taxData?.paymentsOnAccount31stJuly?.value;
    const taxLiabilityOwed = useMemo(() => "£" + Math.abs(taxLiability).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [taxLiability]);
    const paymentOnAccountDueJan = useMemo(() => "£" + Math.abs(paymentOnAccountJan).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [paymentOnAccountJan]);
    const paymentOnAccountDueJuly = useMemo(() => "£" + Math.abs(paymentOnAccountJuly).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [paymentOnAccountJuly]);
    const taxLiabilityRefund = useMemo(() => "£" + Math.abs(taxLiability).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [taxLiability]);
    const handleDownloadPdf = async () => {
        if (!taxData || !currentDocumentYear) return;
        const html = getPdfHtml(currentDocumentYear, user, taxData);
        try {
            const pdfBlob = await generatePdf({ html }).unwrap();
            generatePdfFromBlob(pdfBlob);
        } catch (error) {
            logError(error, {
                message: "Failed to generate tax summary PDF",
                clientId: user?.id,
                function: "handleDownload",
            });
        }
    };
    const handleDownloadWithSpinner = async () => {
        try {
            setIsDownloading(true);
            await handleDownloadPdf();
        } finally {
            setIsDownloading(false);
        }
    };
    useEffect(() => {
        const fetchTaxCalculations = async () => {
            if (!user?.id || !currentDocument?.id) {
                logError("Error: User ID or current document is missing. This might indicate an issue with user authentication or document loading.");
                return;
            }
            try {
                const response = await getGetTaxCalculations({
                    clientId: user.id,
                    documentId: currentDocument.id,
                }).unwrap();
                const breakdown = response.breakdown;
                setTaxData(breakdown);
            } catch (error) {
                const errorContext = {
                    clientId: user?.id,
                    message: "Failed to fetch tax calculations",
                    function: "fetchTaxCalculations",
                };
                logError(error, errorContext);
            }
        };

        fetchTaxCalculations();
    }, [getGetTaxCalculations, user, currentDocument]);

    const showFirstContent = (
        <div className="mb20">
            <Typography className="ready-for-review__title">
                {currentDocumentYear} Tax Return
            </Typography>
            <div>
                <div className="ready-for-review__client-details">
                    <div className="ready-for-review__text-black">Status</div>
                    <div className="ready-for-review__value">
                        <div>Submitted</div>
                    </div>
                </div>
                <div className="ready-for-review__client-details">
                    <div className="ready-for-review__text-black">Date</div>
                    <div className="ready-for-review__value">
                        <div>{date}</div>
                    </div>
                </div>
                {(taxLiability < 0) ? (
                    <div className="ready-for-review__client-details">
                        <div className="ready-for-review__text-green">Estimated Refund Due</div>
                        <div className="ready-for-review__value">
                            <div className="primary-color">{taxLiabilityRefund} </div>
                        </div>
                    </div>)
                    : (<><div className="ready-for-review__client-details">
                        <div className="ready-for-review__text-black">
                            Total Tax Due<br />(Due 31/01/2026)
                        </div>
                        <div className="ready-for-review__value">
                            <div>{taxLiabilityOwed}</div>
                        </div>
                    </div><div className="ready-for-review__client-details">
                        <div className="ready-for-review__text-black">
                                1<sup>st</sup> Payment on Account<br />(Due 31/01/2026)
                        </div>
                        <div className="ready-for-review__value">{paymentOnAccountDueJan}</div>
                    </div><div className="ready-for-review__client-details">
                        <div className="ready-for-review__text-black">
                                2<sup>nd</sup> Payment on Account<br />(Due 31/07/2026)
                        </div>
                        <div className="ready-for-review__value">{paymentOnAccountDueJuly}</div>
                    </div></>)}

                <div className="ready-for-review__client-details vertical-center">
                    <div className="ready-for-review__text-black-pdf"><img src={pdfIcon} alt="pdf icon" className="ready-for-review__download-link"
                        width={"36px"} onClick={handleDownloadWithSpinner}/> SA302 Form.pdf
                    </div>
                    <div className="ready-for-review__value">
                        <div
                            onClick={handleDownloadWithSpinner}
                            className="ready-for-review__download-link"
                        >
                            {isDownloading ? <Spin size="small"/> : "Download"}
                        </div>
                    </div>
                </div>
            </div>
        </div>);

    const showLastContent = (taxLiability < 0) ? (
        <div>
            <div className="mb20">
                <Typography className="ready-for-review__text">
                    If you have saved your bank details with HMRC, your refund will be automatically processed and sent
                    to your account.
                    If HMRC does not have your bank details, they will contact you to arrange the refund.
                </Typography>
            </div>
            <div className="mb20">
                <Typography className="ready-for-review__title">
                When Will You Receive Your Refund?
                </Typography>
                <Typography className="ready-for-review__text">
                HMRC typically processes refunds within 5-10 working days. However, it may take longer during peak times.
                </Typography>
            </div>
            <div className="mb20">
                <Typography className="ready-for-review__title">
                Need Help?
                </Typography>
                <Typography className="ready-for-review__text">
                If you have not received your refund or need assistance, you can contact HMRC directly.
                </Typography>
            </div>
            <div className="mb20">
                <Typography className="ready-for-review__text-link">
                    <img src={openLinkIcon} alt="open link icon" /><a
                        href="https://www.gov.uk/self-assessment-tax-returns"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ready-for-review__link"
                    >Check Your Refund Status</a>
                </Typography>
            </div>
            <div className="mb20">
                <Typography className="ready-for-review__text-link">
                    <img src={openLinkIcon} alt="open link icon" /><a
                        href="https://www.gov.uk/self-assessment-tax-returns"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ready-for-review__link"
                    >Contact HMRC</a>
                </Typography>
            </div>
            <div className="mb20">
                <Typography className="ready-for-review__text-link">
                    <img src={openLinkIcon} alt="open link icon" /><a
                        href="https://www.gov.uk/guidance/claim-to-reduce-payments-on-account"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ready-for-review__link"
                    >Apply to Reduce Payments on Account</a>
                </Typography>
            </div>
        </div>
    ) : (
        <div>
            <div className="mb20">
                <Typography className="ready-for-review__text">
                    You can pay your <strong>Total Tax Due</strong> directly to HMRC using
                    the link below. Payments can be made by bank transfer, debit card, or through your HMRC online account. This amount and its due date are listed in the summary shown above.
                </Typography>
            </div>
            {(paymentOnAccountJan > 0 || paymentOnAccountJuly > 0) && (
                <>
                    <div className="mb20">
                        <Typography className="ready-for-review__title">
                            Your Payments on Account
                        </Typography>
                    </div>
                    <div className="mb20">
                        <Typography className="ready-for-review__text">
                            Separate to the above tax liability, you also must pay your <strong>Payments on Account</strong>. These are pre-payments for next year's tax bill, so that you won't have to pay it all in one go!
                        </Typography>
                    </div>
                    <div className="mb20">
                        <Typography className="ready-for-review__text">
                            Most self-assessed workers must pay these and they are split into two instalments. These instalment amounts and due dates are listed in the summary shown above.
                            You can pay your <strong>Payments on Account</strong> directly to HMRC using the link below.
                        </Typography>
                    </div>
                    <div className="mb20">
                        <Typography className="ready-for-review__text">
                            If you expect your income to be lower next year, you can apply to reduce your <strong>Payments on Account</strong>.
                        </Typography>
                    </div>
                    <div className="mb20">
                        <Typography className="ready-for-review__text-link">
                            <img src={openLinkIcon} alt="open link icon" />
                            <a
                                href="https://www.gov.uk/understand-self-assessment-bill/payments-on-account"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ready-for-review__link"
                            >Learn About Payments on Account</a>
                        </Typography>
                    </div>
                    <div className="mb20">
                        <Typography className="ready-for-review__text-link">
                            <img src={openLinkIcon} alt="open link icon" />
                            <a
                                href="https://www.gov.uk/guidance/claim-to-reduce-payments-on-account"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ready-for-review__link"
                            >Apply to Reduce Payments on Account</a>
                        </Typography>
                    </div>
                </>
            )}
            <div className="mb20">
                <Typography className="ready-for-review__text-link">
                    <img src={openLinkIcon} alt="open link icon" />
                    <a
                        href="https://www.gov.uk/pay-self-assessment-tax-bill"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ready-for-review__link"
                    >Pay Your Tax Due and Payments on Account to HMRC</a>
                </Typography>
            </div>
        </div>
    );

    return (
        <div>
            {isMobile ? (
                <><div className="mb20">
                    {showFirstContent}
                </div>   <Collapse expandIcon={({ isActive }) => (isActive ? <UpOutlined /> : <DownOutlined />)} style={{ border: "none", background: "transparent" }}>
                    <Panel className="ready-for-review__panel ready-for-review__title" header="Paying your Total Tax Due" key="1" style={{ border: "none", borderBottom: "none", background: "transparent" }}>
                        {showLastContent}
                    </Panel>
                </Collapse></>
            ) : (
                <>
                    {showFirstContent}
                    {(taxLiability >= 0) ? <Typography className="ready-for-review__title">
                        Paying your Total Tax Due
                    </Typography> : <Typography className="ready-for-review__title">
                        How to Claim Your Refund
                    </Typography>}
                    {showLastContent}
                </>
            )}
        </div>
    );
}
